import { getAuthContextState } from "@/features/auth/services/auth-context-store"
import { activityApi } from "@/features/activity/services/activity-api"
import {
  ActivityEventType,
  ActivitySeverity,
  ActivityTargetEntityType,
} from "@/features/activity/model/activity-types"
import {
  buildMemberFullName,
  EmploymentType,
  type GetMemberRelationshipsResponse,
  type Member,
  type MemberAdminUpdateInput,
  MemberCreatedVia,
  MemberGender,
  MemberMarriageType,
  type MemberOnboardingInput,
  type MemberProfileUpdateInput,
  MemberRelationshipSuggestionStatus,
  RelationshipDecision,
  MemberRelationshipSuggestionType,
  type MemberRole,
  MemberMaritalStatus,
  MemberStatus,
  type RelationshipBranchSummary,
  type RelationshipMemberSummary,
  type RelationshipWorkspaceSummary,
} from "@/features/members/model/members-types"
import { createInvite as createInviteRecord } from "@/features/members/services/member-invite-service"
import { useMembersStore } from "@/features/members/services/members-store"
import { useTenantSettingsStore } from "@/features/settings/services/general/general-store"
import { http } from "@/shared/lib/api/http"
import { normalizeOverviewResponse } from "@/shared/lib/overview/normalize-overview-response"
import {
  OverviewContext,
  OverviewGranularity,
  OverviewSurface,
  type OverviewResponse,
} from "@ibada/shared-types/overview"

type WorkspaceSummaryApi = {
  id?: string
  name?: string
  slug?: string
  logoUrl?: string | null
  status?: string
}

type BranchSummaryApi = {
  id?: string
  workspaceId?: string
  name?: string
  slug?: string
  status?: "ACTIVE" | "INACTIVE"
  workspace?: WorkspaceSummaryApi | null
}

type MemberApiRecord = {
  id?: string
  workspaceId?: string
  branchId?: string
  firstName?: string
  middleName?: string
  middle_name?: string
  lastName?: string
  email?: string | null
  phone?: string | null
  occupation?: string | null
  avatarUrl?: string | null
  customFields?: Record<string, unknown> | null
  workspace?: WorkspaceSummaryApi | null
  branch?: BranchSummaryApi | null
  role?: string | null
  roleKey?: string | null
  team?: string | null
  status?: string | null
  createdVia?: string | null
  created_via?: string | null
  employmentType?: string | null
  employment_type?: string | null
  name?: string
  gender?: string | null
  maritalStatus?: string | null
  marital_status?: string | null
  marriageType?: string | null
  marriage_type?: string | null
  baptized?: boolean | null
  baptizedAt?: string | null
  baptized_at?: string | null
  baptismPlace?: string | null
  baptism_place?: string | null
  originChurchName?: string | null
  origin_church_name?: string | null
  fatherFirstName?: string | null
  father_first_name?: string | null
  fatherMiddleName?: string | null
  father_middle_name?: string | null
  fatherLastName?: string | null
  father_last_name?: string | null
  motherFirstName?: string | null
  mother_first_name?: string | null
  motherMiddleName?: string | null
  mother_middle_name?: string | null
  motherLastName?: string | null
  mother_last_name?: string | null
  spouseFirstName?: string | null
  spouse_first_name?: string | null
  spouseMiddleName?: string | null
  spouse_middle_name?: string | null
  spouseLastName?: string | null
  spouse_last_name?: string | null
  guardianFirstName?: string | null
  guardian_first_name?: string | null
  guardianMiddleName?: string | null
  guardian_middle_name?: string | null
  guardianLastName?: string | null
  guardian_last_name?: string | null
  dateOfBirth?: string | null
  date_of_birth?: string | null
  placeOfBirthCountry?: string | null
  place_of_birth_country?: string | null
  placeOfBirthCity?: string | null
  place_of_birth_city?: string | null
  placeOfBirth?: string | null
  place_of_birth?: string | null
  address?: string | null
  notes?: string | null
  locale?: string | null
  timezone?: string | null
  groupIds?: unknown
  group_ids?: unknown
  groupNames?: unknown
  group_names?: unknown
  groups?: unknown
  ministryIds?: unknown
  ministry_ids?: unknown
  ministryNames?: unknown
  ministry_names?: unknown
  ministries?: unknown
  ministryTypes?: unknown
  ministry_types?: unknown
  createdAt?: string
  updatedAt?: string
}

type MemberRelationshipApiRecord = {
  id?: string
  workspaceId?: string
  branchId?: string | null
  memberId?: string
  relatedMemberId?: string
  suggestionType?: string
  label?: string | null
  displayLabel?: string | null
  reason?: string | null
  confidenceScore?: number | string
  reasons?: unknown
  status?: string
  computedAt?: string
  createdAt?: string
  updatedAt?: string
  relatedMember?: MemberRelationshipMemberSummaryApi | null
}

type MemberRelationshipWorkspaceSummaryApi = {
  id?: string
  name?: string
  slug?: string
  logoUrl?: string | null
  status?: string
}

type MemberRelationshipBranchSummaryApi = {
  id?: string
  workspaceId?: string
  name?: string
  slug?: string
  status?: "ACTIVE" | "INACTIVE"
  workspace?: MemberRelationshipWorkspaceSummaryApi | null
}

type MemberRelationshipMemberSummaryApi = {
  id?: string
  workspaceId?: string
  branchId?: string
  firstName?: string
  middleName?: string | null
  middle_name?: string | null
  lastName?: string
  gender?: string | null
  status?: string | null
  email?: string | null
  phone?: string | null
  avatarUrl?: string | null
  branch?: MemberRelationshipBranchSummaryApi | null
}

type MemberRelationshipsApiResponse = {
  member?: MemberRelationshipMemberSummaryApi | null
  items?: unknown
}

export type ListMembersQuery = {
  workspaceId?: string
  branchId?: string
  /** Server-side filter (name, email, phone). */
  q?: string
  search?: string
  page?: number
  limit?: number
  sortBy?: "createdAt" | "updatedAt" | "firstName" | "lastName" | "status"
  sortOrder?: "asc" | "desc"
  filters?: string
  segment?: "members" | "visitors" | "pending_confirmation"
}

export type ListMembersResponse = {
  items: Member[]
  total: number
  page: number
  limit: number
}

export type MembersImportRelatedValue = {
  name: string
}

export type MembersImportScopeOption = "BRANCH" | "SHARED"

export type MembersImportResolutionAction = "CREATE" | "MAP" | "IGNORE"

export type MembersImportResolution = {
  detectedName: string
  action: MembersImportResolutionAction
  targetId?: string
}

export type MembersImportResolutionSummary = {
  detectedName: string
  targetId: string
  targetName: string
}

export type MembersImportIssue = {
  code: string
  message: string
  params?: Record<string, string | number>
}

export type MembersImportRowError = {
  row: number
  kind: "invalid" | "duplicate"
  messages: string[]
  issues: MembersImportIssue[]
  fieldErrors?: Record<string, string[]>
  fieldIssues?: Record<string, MembersImportIssue[]>
  rowData?: Record<string, string | null>
}

export type MembersImportAnalysis = {
  headers: string[]
  matchesTemplate: boolean
  mappingRequired: boolean
  canProceed: boolean
  canCreateGroups: boolean
  groupScopeOptions: MembersImportScopeOption[]
  currentBranchName: string
  invalidBranchValues: string[]
  requiresBranchReplacement: boolean
  detectedFields: Record<string, string | null>
  missingRequiredFields: string[]
  totalRows: number
  validRows: number
  invalidRows: number
  duplicateRows: number
  // Rows that carry a known member id and will UPDATE an existing member (round-trip re-upload).
  updateRows?: number
  newGroups: MembersImportRelatedValue[]
  errors: MembersImportRowError[]
}

export type MembersImportConfirmResult = {
  createdMembersCount: number
  // Existing members updated via a matched id (round-trip re-upload). Optional until the backend reports it.
  updatedMembersCount?: number
  createdGroupsCount: number
  skippedRowsCount: number
  duplicateRowsCount: number
  totalRows: number
  hasFailures: boolean
  hasSuccesses: boolean
  createdGroups: string[]
  mappedGroups: MembersImportResolutionSummary[]
  ignoredGroups: string[]
  errors: MembersImportRowError[]
}

function extractFilename(contentDisposition: string | undefined): string | null {
  if (!contentDisposition) return null

  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1])
    } catch {
      return utfMatch[1]
    }
  }

  const asciiMatch = /filename="?([^";]+)"?/i.exec(contentDisposition)
  if (asciiMatch?.[1]) {
    return asciiMatch[1]
  }

  return null
}

export type MembersImportReferenceField = {
  key: string
  label: string
  required: boolean
  allowedValues: string[]
}

/** Accepted values for the import: enum field options (localized) + the existing groups to match. */
export type MembersImportReference = {
  language: string
  fields: MembersImportReferenceField[]
  groups: string[]
}

export type MembersOverviewResponse = OverviewResponse

export type MembersOverviewQuery = {
  workspaceId?: string
  context?: OverviewContext
  profile?: OverviewSurface
  branchId?: string
  dateFrom?: string
  dateTo?: string
  granularity?: OverviewGranularity
  limit?: number
  status?: string
  groupId?: string
  ministryId?: string
  gender?: string
  creationSource?: string
}

function normalizeString(value: unknown) {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeNullableString(value: unknown) {
  if (value === null || value === undefined) return undefined
  return normalizeString(value)
}

function uniqueStrings(values: Array<string | undefined>) {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value))))
}

function extractCandidateId(value: unknown) {
  if (typeof value === "string") {
    return normalizeString(value)
  }

  if (typeof value !== "object" || value === null) {
    return undefined
  }

  const record = value as Record<string, unknown>
  return normalizeString(record.id) ?? normalizeString(record.groupId) ?? normalizeString(record.ministryId)
}

function extractCandidateName(value: unknown) {
  if (typeof value === "string") {
    return normalizeString(value)
  }

  if (typeof value !== "object" || value === null) {
    return undefined
  }

  const record = value as Record<string, unknown>
  return normalizeString(record.name) ?? normalizeString(record.label) ?? normalizeString(record.title)
}

function toCollection(value: unknown) {
  if (value === null || value === undefined) return []
  return Array.isArray(value) ? value : [value]
}

function extractIdList(value: unknown) {
  return uniqueStrings(toCollection(value).map(extractCandidateId))
}

function extractNameList(value: unknown) {
  return uniqueStrings(toCollection(value).map(extractCandidateName))
}

function normalizeIsoDate(value: unknown, fallback: string) {
  const candidate = normalizeString(value)
  if (!candidate) return fallback
  const parsed = new Date(candidate)
  if (Number.isNaN(parsed.getTime())) return fallback
  return parsed.toISOString()
}

function normalizeNumber(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string") {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

function normalizeMemberStatus(value: unknown): MemberStatus {
  const normalized = normalizeString(value)?.toLowerCase()
  if (normalized === MemberStatus.Inactive) return MemberStatus.Inactive
  if (normalized === MemberStatus.Visitor) return MemberStatus.Visitor
  if (normalized === MemberStatus.Invited) return MemberStatus.Invited
  if (normalized === MemberStatus.PendingConfirmation) return MemberStatus.PendingConfirmation
  if (normalized === MemberStatus.Suspended) return MemberStatus.Suspended
  if (normalized === MemberStatus.Deceased) return MemberStatus.Deceased
  if (normalized === MemberStatus.Deactivated) return MemberStatus.Deactivated
  return MemberStatus.Active
}

function normalizeMemberCreatedVia(value: unknown): MemberCreatedVia | undefined {
  const normalized = normalizeString(value)
  if (normalized === MemberCreatedVia.Link) return MemberCreatedVia.Link
  if (normalized === MemberCreatedVia.Import) return MemberCreatedVia.Import
  if (normalized === MemberCreatedVia.Dashboard) return MemberCreatedVia.Dashboard
  return undefined
}

function normalizeMemberMaritalStatus(value: unknown): MemberMaritalStatus | undefined {
  const normalized = normalizeString(value)
  if (!normalized) return undefined

  const upper = normalized.toUpperCase()
  if (upper === MemberMaritalStatus.Single) return MemberMaritalStatus.Single
  if (upper === MemberMaritalStatus.Married) return MemberMaritalStatus.Married
  if (upper === MemberMaritalStatus.Engaged) return MemberMaritalStatus.Engaged
  if (upper === MemberMaritalStatus.Divorced) return MemberMaritalStatus.Divorced
  if (upper === MemberMaritalStatus.Widowed) return MemberMaritalStatus.Widowed
  if (upper === MemberMaritalStatus.Separated) return MemberMaritalStatus.Separated
  if (upper === MemberMaritalStatus.Unknown) return MemberMaritalStatus.Unknown
  return undefined
}

function normalizeMemberMarriageType(value: unknown): MemberMarriageType | undefined {
  const normalized = normalizeString(value)
  if (!normalized) return undefined

  const upper = normalized.toUpperCase()
  if (upper === MemberMarriageType.None) return MemberMarriageType.None
  if (upper === MemberMarriageType.ChurchOnly) return MemberMarriageType.ChurchOnly
  if (upper === MemberMarriageType.CivilOnly) return MemberMarriageType.CivilOnly
  if (upper === MemberMarriageType.ChurchAndCivil) return MemberMarriageType.ChurchAndCivil
  if (upper === MemberMarriageType.TraditionalOnly) return MemberMarriageType.TraditionalOnly
  if (upper === MemberMarriageType.Unknown) return MemberMarriageType.Unknown
  return undefined
}



function normalizeEmploymentType(value: unknown): EmploymentType {
  const normalized = normalizeString(value)?.toLowerCase()
  if (normalized === EmploymentType.Contractor) return EmploymentType.Contractor
  if (normalized === EmploymentType.Intern) return EmploymentType.Intern
  return EmploymentType.FullTime
}

function normalizeRelationshipSuggestionType(value: unknown): MemberRelationshipSuggestionType {
  const normalized = normalizeString(value)?.toUpperCase()
  if (normalized === MemberRelationshipSuggestionType.SameFamily) return MemberRelationshipSuggestionType.SameFamily
  if (normalized === MemberRelationshipSuggestionType.PossibleSibling) return MemberRelationshipSuggestionType.PossibleSibling
  if (normalized === MemberRelationshipSuggestionType.PossibleParentChild) return MemberRelationshipSuggestionType.PossibleParentChild
  if (normalized === MemberRelationshipSuggestionType.PossibleSpouse) return MemberRelationshipSuggestionType.PossibleSpouse
  if (normalized === MemberRelationshipSuggestionType.PossibleHouseholdMember) {
    return MemberRelationshipSuggestionType.PossibleHouseholdMember
  }
  return MemberRelationshipSuggestionType.SameFamily
}

function normalizeRelationshipSuggestionStatus(value: unknown): MemberRelationshipSuggestionStatus {
  const normalized = normalizeString(value)?.toUpperCase()
  if (normalized === MemberRelationshipSuggestionStatus.Inactive) return MemberRelationshipSuggestionStatus.Inactive
  if (normalized === MemberRelationshipSuggestionStatus.Dismissed) return MemberRelationshipSuggestionStatus.Dismissed
  if (normalized === MemberRelationshipSuggestionStatus.Confirmed) return MemberRelationshipSuggestionStatus.Confirmed
  return MemberRelationshipSuggestionStatus.Active
}

function normalizeRelationshipWorkspaceSummary(
  input: MemberRelationshipWorkspaceSummaryApi | null | undefined
): RelationshipWorkspaceSummary | null {
  if (!input || typeof input !== "object") return null

  return {
    id: normalizeString(input.id) ?? "",
    name: normalizeString(input.name) ?? "",
    slug: normalizeString(input.slug) ?? "",
    logoUrl: normalizeNullableString(input.logoUrl) ?? null,
    status: normalizeString(input.status) ?? "",
  }
}

function normalizeRelationshipBranchSummary(
  input: MemberRelationshipBranchSummaryApi | null | undefined
): RelationshipBranchSummary | null {
  if (!input || typeof input !== "object") return null

  return {
    id: normalizeString(input.id) ?? "",
    workspaceId: normalizeString(input.workspaceId) ?? "",
    name: normalizeString(input.name) ?? "",
    slug: normalizeString(input.slug) ?? "",
    status: input.status === "INACTIVE" ? "INACTIVE" : "ACTIVE",
    workspace: normalizeRelationshipWorkspaceSummary(input.workspace),
  }
}

function normalizeRelationshipMemberSummary(
  input: MemberRelationshipMemberSummaryApi | null | undefined
): RelationshipMemberSummary | null {
  if (!input || typeof input !== "object") return null

  const firstName = normalizeString(input.firstName) ?? ""
  const middleName = normalizeNullableString(input.middle_name) ?? normalizeNullableString(input.middleName) ?? null
  const lastName = normalizeString(input.lastName) ?? ""

  return {
    id: normalizeString(input.id) ?? "",
    workspaceId: normalizeString(input.workspaceId) ?? "",
    branchId: normalizeString(input.branchId) ?? "",
    firstName,
    middleName,
    lastName,
    gender:
      normalizeString(input.gender)?.toLowerCase() === MemberGender.Female
        ? MemberGender.Female
        : normalizeString(input.gender)?.toLowerCase() === MemberGender.Male
          ? MemberGender.Male
          : null,
    status: normalizeMemberStatus(input.status),
    email: normalizeNullableString(input.email) ?? null,
    phone: normalizeNullableString(input.phone) ?? null,
    avatarUrl: normalizeNullableString(input.avatarUrl) ?? null,
    branch: normalizeRelationshipBranchSummary(input.branch),
  }
}

function normalizeMemberRelationshipItem(input: unknown) {
  const record = (input ?? {}) as MemberRelationshipApiRecord
  const nowIso = new Date().toISOString()

  return {
    id: normalizeString(record.id) ?? crypto.randomUUID(),
    workspaceId: normalizeString(record.workspaceId) ?? "",
    branchId: normalizeNullableString(record.branchId) ?? null,
    memberId: normalizeString(record.memberId) ?? "",
    relatedMemberId: normalizeString(record.relatedMemberId) ?? "",
    suggestionType: normalizeRelationshipSuggestionType(record.suggestionType),
    displayLabel: normalizeNullableString(record.displayLabel) ?? normalizeNullableString(record.label) ?? null,
    reasonText: normalizeNullableString(record.reason) ?? null,
    confidenceScore: normalizeNumber(record.confidenceScore) ?? 0,
    reasons: record.reasons ?? null,
    status: normalizeRelationshipSuggestionStatus(record.status),
    computedAt: normalizeIsoDate(record.computedAt, nowIso),
    createdAt: normalizeIsoDate(record.createdAt, nowIso),
    updatedAt: normalizeIsoDate(record.updatedAt, nowIso),
    relatedMember: normalizeRelationshipMemberSummary(record.relatedMember),
  }
}

function normalizeMemberRelationships(
  input: MemberRelationshipsApiResponse | Record<string, unknown> | unknown
): GetMemberRelationshipsResponse {
  const record = (input ?? {}) as MemberRelationshipsApiResponse & Record<string, unknown>
  const member = normalizeRelationshipMemberSummary(record.member)
  const items = Array.isArray(record.items)
    ? record.items.map(normalizeMemberRelationshipItem)
    : []

  return {
    member: member ?? {
      id: "",
      workspaceId: "",
      branchId: "",
      firstName: "",
      middleName: null,
      lastName: "",
      gender: null,
      status: MemberStatus.Active,
      email: null,
      phone: null,
      avatarUrl: null,
      branch: null,
    },
    items,
  }
}

function normalizeRole(value: unknown): MemberRole {
  const normalized = normalizeString(value)?.toLowerCase()
  if (normalized === "admin") return "admin"
  if (normalized === "finance") return "finance"
  if (normalized === "hr") return "hr"
  if (normalized === "operations") return "operations"
  if (normalized === "storekeeper") return "storekeeper"
  return "viewer"
}

function resolveDefaultTeam() {
  const configuredTeam = normalizeString(
    useTenantSettingsStore.getState().getSettings().people.defaultTeam
  )
  return configuredTeam ?? "Community"
}

function resolveWorkspaceId() {
  const authContext = getAuthContextState()
  return (
    authContext.activeWorkspaceId ??
    authContext.authMe?.workspaceAccess?.[0]?.workspace?.id ??
    null
  )
}

export function buildMembersListParams(query?: ListMembersQuery) {
  const params: Record<string, string> = {}
  const workspaceId = normalizeString(query?.workspaceId) ?? resolveWorkspaceId() ?? undefined
  const branchId = normalizeString(query?.branchId)
  const search = normalizeString(query?.q ?? query?.search)
  const page = typeof query?.page === "number" ? query.page : undefined
  const limit = typeof query?.limit === "number" ? query.limit : undefined
  const sortBy = normalizeString(query?.sortBy)
  const sortOrder = normalizeString(query?.sortOrder)

  if (workspaceId) {
    params.workspaceId = workspaceId
  }
  if (branchId) {
    params.branchId = branchId
  }
  if (search) {
    params.search = search
  }
  if (page) {
    params.page = String(page)
  }
  if (limit) {
    params.limit = String(limit)
  }
  if (sortBy) {
    params.sortBy = sortBy
  }
  if (sortOrder) {
    params.sortOrder = sortOrder
  }

  if (!params.workspaceId && !params.branchId) {
    throw new Error("Members list requires workspaceId or branchId.")
  }

  const segment = normalizeString(query?.segment)
  const filters = normalizeString(query?.filters)
  if (segment || filters) {
    const baseFilters = filters ? safeParseJsonObject(filters) : {}
    if (segment) baseFilters.segment = segment
    params.filters = JSON.stringify(baseFilters)
  }

  return params
}

function safeParseJsonObject(value: string) {
  try {
    const parsed = JSON.parse(value)
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
  } catch {
    return {}
  }
  return {}
}

function extractMemberRecord(payload: unknown): MemberApiRecord {
  if (Array.isArray(payload)) {
    const first = payload[0]
    return typeof first === "object" && first !== null ? (first as MemberApiRecord) : {}
  }

  if (typeof payload !== "object" || payload === null) {
    return {}
  }

  const record = payload as Record<string, unknown>
  for (const key of ["member", "item", "result", "data"]) {
    const value = record[key]
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      return value as MemberApiRecord
    }
  }

  return record as MemberApiRecord
}

function extractMemberRecords(payload: unknown): MemberApiRecord[] {
  if (Array.isArray(payload)) {
    return payload.filter((entry): entry is MemberApiRecord => typeof entry === "object" && entry !== null)
  }

  if (typeof payload !== "object" || payload === null) {
    return []
  }

  const record = payload as Record<string, unknown>
  const directCollections = [record.items, record.members, record.data]
  for (const candidate of directCollections) {
    if (Array.isArray(candidate)) {
      return candidate.filter((entry): entry is MemberApiRecord => typeof entry === "object" && entry !== null)
    }
  }

  if (typeof record.data === "object" && record.data !== null) {
    const nested = record.data as Record<string, unknown>
    const nestedCollections = [nested.items, nested.members]
    for (const candidate of nestedCollections) {
      if (Array.isArray(candidate)) {
        return candidate.filter((entry): entry is MemberApiRecord => typeof entry === "object" && entry !== null)
      }
    }
  }

  return []
}

function normalizeMember(record: MemberApiRecord, index: number): Member {
  const id = normalizeString(record.id) ?? `member-${index + 1}`
  const nowIso = new Date().toISOString()
  const createdAt = normalizeIsoDate(record.createdAt, nowIso)
  const updatedAt = normalizeIsoDate(record.updatedAt, createdAt)
  const existing = useMembersStore.getState().getMemberById(id)

  const firstName = normalizeString(record.firstName) ?? existing?.firstName ?? ""
  const middleName =
    normalizeNullableString(record.middle_name) ??
    normalizeNullableString(record.middleName) ??
    existing?.middleName
  const lastName = normalizeString(record.lastName) ?? existing?.lastName ?? ""
  const groupIdsFromApi = extractIdList(record.groupIds ?? record.group_ids ?? record.groups)
  const ministryIdsFromApi = extractIdList(
    record.ministryIds ?? record.ministry_ids ?? record.ministries ?? record.ministryTypes ?? record.ministry_types
  )
  const groupNamesFromApi = extractNameList(record.groupNames ?? record.group_names ?? record.groups)
  const ministryNamesFromApi = extractNameList(
    record.ministryNames ??
      record.ministry_names ??
      record.ministries ??
      record.ministryTypes ??
      record.ministry_types
  )
  const placeOfBirthCountry =
    normalizeNullableString(record.place_of_birth_country) ??
    normalizeNullableString(record.placeOfBirthCountry)
  const placeOfBirthCity =
    normalizeNullableString(record.place_of_birth_city) ??
    normalizeNullableString(record.placeOfBirthCity)
  const derivedPlaceOfBirthFromParts = [placeOfBirthCountry, placeOfBirthCity].filter(Boolean).join(", ")

  return {
    id,
    workspaceId: normalizeString(record.workspaceId) ?? normalizeString(record.workspace?.id) ?? existing?.workspaceId,
    name: buildMemberFullName({
      firstName,
      middleName: middleName ?? undefined,
      lastName,
      fallback: existing?.name,
    }),
    firstName,
    middleName: middleName ?? undefined,
    lastName,
    email: normalizeNullableString(record.email) ?? existing?.email ?? null,
    phone: normalizeNullableString(record.phone) ?? existing?.phone,
    occupation: normalizeNullableString(record.occupation) ?? existing?.occupation ?? null,
    address: normalizeNullableString(record.address) ?? existing?.address,
    notes: normalizeNullableString(record.notes) ?? existing?.notes,
    customFields:
      (record.customFields as Record<string, unknown> | null | undefined) ?? existing?.customFields ?? null,
    locale: normalizeNullableString(record.locale) as Member["locale"] | undefined ?? existing?.locale,
    timezone: normalizeNullableString(record.timezone) ?? existing?.timezone,
    gender: normalizeNullableString(record.gender) as Member["gender"] | undefined ?? existing?.gender,
    dateOfBirth:
      normalizeNullableString(record.date_of_birth) ??
      normalizeNullableString(record.dateOfBirth) ??
      existing?.dateOfBirth,
    placeOfBirth:
      normalizeNullableString(record.place_of_birth) ??
      normalizeNullableString(record.placeOfBirth) ??
      normalizeNullableString(derivedPlaceOfBirthFromParts) ??
      existing?.placeOfBirth,
    placeOfBirthCountry: placeOfBirthCountry ?? existing?.placeOfBirthCountry,
    placeOfBirthCity: placeOfBirthCity ?? existing?.placeOfBirthCity,
    maritalStatus:
      normalizeMemberMaritalStatus(record.marital_status) ??
      normalizeMemberMaritalStatus(record.maritalStatus) ??
      existing?.maritalStatus,
    marriageType:
      normalizeMemberMarriageType(record.marriage_type) ??
      normalizeMemberMarriageType(record.marriageType) ??
      existing?.marriageType,
    baptized: record.baptized ?? existing?.baptized,
    baptizedAt:
      normalizeNullableString(record.baptized_at) ??
      normalizeNullableString(record.baptizedAt) ??
      existing?.baptizedAt,
    baptismPlace:
      normalizeNullableString(record.baptism_place) ??
      normalizeNullableString(record.baptismPlace) ??
      existing?.baptismPlace,
    originChurchName:
      normalizeNullableString(record.origin_church_name) ??
      normalizeNullableString(record.originChurchName) ??
      existing?.originChurchName,
    fatherFirstName:
      normalizeNullableString(record.father_first_name) ??
      normalizeNullableString(record.fatherFirstName) ??
      existing?.fatherFirstName,
    fatherMiddleName:
      normalizeNullableString(record.father_middle_name) ??
      normalizeNullableString(record.fatherMiddleName) ??
      existing?.fatherMiddleName,
    fatherLastName:
      normalizeNullableString(record.father_last_name) ??
      normalizeNullableString(record.fatherLastName) ??
      existing?.fatherLastName,
    motherFirstName:
      normalizeNullableString(record.mother_first_name) ??
      normalizeNullableString(record.motherFirstName) ??
      existing?.motherFirstName,
    motherMiddleName:
      normalizeNullableString(record.mother_middle_name) ??
      normalizeNullableString(record.motherMiddleName) ??
      existing?.motherMiddleName,
    motherLastName:
      normalizeNullableString(record.mother_last_name) ??
      normalizeNullableString(record.motherLastName) ??
      existing?.motherLastName,
    spouseFirstName:
      normalizeNullableString(record.spouse_first_name) ??
      normalizeNullableString(record.spouseFirstName) ??
      existing?.spouseFirstName,
    spouseMiddleName:
      normalizeNullableString(record.spouse_middle_name) ??
      normalizeNullableString(record.spouseMiddleName) ??
      existing?.spouseMiddleName,
    spouseLastName:
      normalizeNullableString(record.spouse_last_name) ??
      normalizeNullableString(record.spouseLastName) ??
      existing?.spouseLastName,
    guardianFirstName:
      normalizeNullableString(record.guardian_first_name) ??
      normalizeNullableString(record.guardianFirstName) ??
      existing?.guardianFirstName,
    guardianMiddleName:
      normalizeNullableString(record.guardian_middle_name) ??
      normalizeNullableString(record.guardianMiddleName) ??
      existing?.guardianMiddleName,
    guardianLastName:
      normalizeNullableString(record.guardian_last_name) ??
      normalizeNullableString(record.guardianLastName) ??
      existing?.guardianLastName,
    avatarUrl:
      normalizeNullableString(record.avatarUrl) ??
      existing?.avatarUrl,
    branchId:
      normalizeString(record.branchId) ??
      normalizeString(record.branch?.id) ??
      existing?.branchId,
    role: normalizeRole(record.role ?? record.roleKey ?? existing?.role),
    team: normalizeNullableString(record.team) ?? existing?.team ?? resolveDefaultTeam(),
    status: normalizeMemberStatus(record.status ?? existing?.status),
    createdVia:
      normalizeMemberCreatedVia(record.created_via) ??
      normalizeMemberCreatedVia(record.createdVia) ??
      existing?.createdVia,
    employmentType: normalizeEmploymentType(record.employment_type ?? record.employmentType ?? existing?.employmentType),
    groupIds: groupIdsFromApi,
    ministryIds: ministryIdsFromApi,
    groupNames: groupNamesFromApi,
    ministryNames: ministryNamesFromApi,
    source: "backend",
    lastActiveAt: updatedAt,
    joinedAt: createdAt,
  }
}

async function getMembersOverview(query?: MembersOverviewQuery): Promise<MembersOverviewResponse> {
  const workspaceId = normalizeString(query?.workspaceId) ?? resolveWorkspaceId() ?? undefined
  if (!workspaceId) throw new Error("Members overview requires workspaceId.")

  const params: Record<string, string> = {
    workspaceId,
  }

  const context = query?.context ?? OverviewContext.Branch
  params.context = context

  if (context === OverviewContext.Branch && query?.branchId) {
    params.branchId = query.branchId
  }

  if (query?.dateFrom && query?.dateTo) {
    params.dateFrom = query.dateFrom
    params.dateTo = query.dateTo
  }

  params.granularity = query?.granularity ?? OverviewGranularity.Monthly
  params.profile = query?.profile ?? OverviewSurface.Full

  if (typeof query?.limit === "number" && query.limit > 0) {
    params.limit = String(Math.min(query.limit, 100))
  }

  if (query?.status) params.status = query.status
  if (query?.groupId) params.groupId = query.groupId
  if (query?.ministryId) params.ministryId = query.ministryId
  if (query?.gender) params.gender = query.gender
  if (query?.creationSource) params.creationSource = query.creationSource

  const response = await http.get<unknown>("/members/overview", { params })


  return normalizeOverviewResponse(response.data)
}

async function listMembers(query?: ListMembersQuery): Promise<ListMembersResponse> {
  const response = await http.get<unknown>("/members", {
    params: buildMembersListParams(query),
  })

  const records = extractMemberRecords(response.data)
  const members = records.map(normalizeMember)
  const total = extractNumericField(response.data, ["total"]) ?? members.length
  const page = extractNumericField(response.data, ["page"]) ?? (query?.page ?? 1)
  const limit = extractNumericField(response.data, ["limit"]) ?? (query?.limit ?? members.length)

  const hasSearch = Boolean(normalizeString(query?.q ?? query?.search))
  const hasFilters = Boolean(normalizeString(query?.filters) || normalizeString(query?.segment))
  if (!hasSearch && !hasFilters && page === 1 && limit >= total) {
    useMembersStore.getState().replaceMembers(members)
  }

  return { items: members, total, page, limit }
}

function extractNumericField(payload: unknown, keys: string[]) {
  if (typeof payload !== "object" || payload === null) return null
  const record = payload as Record<string, unknown>
  for (const key of keys) {
    const value = record[key]
    if (typeof value === "number" && Number.isFinite(value)) return value
  }
  if (typeof record.data === "object" && record.data !== null) {
    const nested = record.data as Record<string, unknown>
    for (const key of keys) {
      const value = nested[key]
      if (typeof value === "number" && Number.isFinite(value)) return value
    }
  }
  return null
}

async function getMemberById(id: string) {
  const response = await http.get<unknown>(`/members/${id}`)
  const member = normalizeMember(extractMemberRecord(response.data), 0)
  useMembersStore.getState().upsertMember(member)
  return member
}

async function getMemberRelationships(memberId: string) {
  const response = await http.get<unknown>(`/members/${memberId}/relationships`)
  return normalizeMemberRelationships(response.data)
}

async function decideRelationship(memberId: string, suggestionId: string, decision: RelationshipDecision) {
  await http.patch(`/members/${memberId}/relationships/${suggestionId}`, { decision })
}

function hasUsableMemberPayload(record: MemberApiRecord) {
  if (normalizeString(record.id)) return true

  const signalFields: Array<unknown> = [
    record.firstName,
    record.lastName,
    record.email,
    record.phone,
    record.status,
    record.branchId,
    record.branch?.id,
  ]

  return signalFields.some((value) => value !== undefined && value !== null && normalizeString(value) !== undefined)
}

function normalizePatchString(value: unknown) {
  if (value === undefined) return undefined
  if (value === null) return null
  const normalized = normalizeString(value)
  return normalized ?? null
}

function mapMemberPatchToApiPayload(id: string, patch: Partial<Member>) {
  const payload: Record<string, unknown> = {}

  if (patch.workspaceId !== undefined) {
    const workspaceId = normalizeString(patch.workspaceId)
    if (!workspaceId) throw new Error("Workspace is required.")
    payload.workspaceId = workspaceId
  }

  if (patch.firstName !== undefined) {
    const firstName = normalizeString(patch.firstName)
    if (!firstName) throw new Error("First name is required.")
    payload.firstName = firstName
  }

  if (patch.middleName !== undefined) payload.middleName = normalizePatchString(patch.middleName)

  if (patch.lastName !== undefined) {
    const lastName = normalizeString(patch.lastName)
    if (!lastName) throw new Error("Last name is required.")
    payload.lastName = lastName
  }

  if (patch.email !== undefined) payload.email = normalizePatchString(patch.email)
  if (patch.phone !== undefined) payload.phone = normalizePatchString(patch.phone)
  if (patch.occupation !== undefined) payload.occupation = normalizePatchString(patch.occupation)
  if (patch.avatarUrl !== undefined) payload.avatarUrl = normalizePatchString(patch.avatarUrl)
  if (patch.address !== undefined) payload.address = normalizePatchString(patch.address)
  if (patch.notes !== undefined) payload.notes = normalizePatchString(patch.notes)
  if (patch.dateOfBirth !== undefined) payload.dateOfBirth = normalizePatchString(patch.dateOfBirth)
  if (patch.placeOfBirth !== undefined) payload.placeOfBirth = normalizePatchString(patch.placeOfBirth)
  if (patch.placeOfBirthCountry !== undefined) payload.placeOfBirthCountry = normalizePatchString(patch.placeOfBirthCountry)
  if (patch.placeOfBirthCity !== undefined) payload.placeOfBirthCity = normalizePatchString(patch.placeOfBirthCity)

  if (patch.branchId !== undefined) {
    const branchId = normalizeString(patch.branchId)
    if (!branchId) throw new Error("Branch is required.")
    payload.branchId = branchId
  }

  if (patch.status !== undefined) payload.status = patch.status
  if (patch.gender !== undefined) payload.gender = patch.gender
  if (patch.maritalStatus !== undefined) payload.maritalStatus = patch.maritalStatus
  if (patch.marriageType !== undefined) payload.marriageType = patch.marriageType
  if (patch.baptized !== undefined) payload.baptized = patch.baptized
  if (patch.baptizedAt !== undefined) payload.baptizedAt = normalizePatchString(patch.baptizedAt)
  if (patch.baptismPlace !== undefined) payload.baptismPlace = normalizePatchString(patch.baptismPlace)
  if (patch.fatherFirstName !== undefined) payload.fatherFirstName = normalizePatchString(patch.fatherFirstName)
  if (patch.fatherMiddleName !== undefined) payload.fatherMiddleName = normalizePatchString(patch.fatherMiddleName)
  if (patch.fatherLastName !== undefined) payload.fatherLastName = normalizePatchString(patch.fatherLastName)
  if (patch.motherFirstName !== undefined) payload.motherFirstName = normalizePatchString(patch.motherFirstName)
  if (patch.motherMiddleName !== undefined) payload.motherMiddleName = normalizePatchString(patch.motherMiddleName)
  if (patch.motherLastName !== undefined) payload.motherLastName = normalizePatchString(patch.motherLastName)
  if (patch.spouseFirstName !== undefined) payload.spouseFirstName = normalizePatchString(patch.spouseFirstName)
  if (patch.spouseMiddleName !== undefined) payload.spouseMiddleName = normalizePatchString(patch.spouseMiddleName)
  if (patch.spouseLastName !== undefined) payload.spouseLastName = normalizePatchString(patch.spouseLastName)
  if (patch.guardianFirstName !== undefined) payload.guardianFirstName = normalizePatchString(patch.guardianFirstName)
  if (patch.guardianMiddleName !== undefined) payload.guardianMiddleName = normalizePatchString(patch.guardianMiddleName)
  if (patch.guardianLastName !== undefined) payload.guardianLastName = normalizePatchString(patch.guardianLastName)

  if (patch.groupIds !== undefined) {
    payload.groupIds = uniqueStrings((patch.groupIds ?? []).map((value) => normalizeString(value)))
  }

  if (patch.ministryIds !== undefined) {
    payload.ministryIds = uniqueStrings((patch.ministryIds ?? []).map((value) => normalizeString(value)))
  }

  if (patch.customFields !== undefined) payload.customFields = patch.customFields

  if (Object.keys(payload).length === 0) {
    throw new Error(`No updatable fields were provided for member ${id}.`)
  }

  return payload
}

function mergeMemberPatch(member: Member, patch: Partial<Member>): Member {
  const next = { ...member, ...patch }

  const firstName = next.firstName ?? member.firstName
  const middleName = next.middleName ?? member.middleName
  const lastName = next.lastName ?? member.lastName

  return {
    ...next,
    firstName,
    middleName,
    lastName,
    name: buildMemberFullName({
      firstName,
      middleName,
      lastName,
      fallback: next.name,
    }),
  }
}

async function updateMember(id: string, patch: Partial<Member>) {
  const payload = mapMemberPatchToApiPayload(id, patch)
  const response = await http.patch<unknown>(`/members/${id}`, payload)
  const record = extractMemberRecord(response.data)

  if (hasUsableMemberPayload(record)) {
    const normalized = normalizeMember(
      {
        ...record,
        id: normalizeString(record.id) ?? id,
      },
      0
    )
    useMembersStore.getState().upsertMember(normalized)
    return normalized
  }

  const existing = useMembersStore.getState().getMemberById(id)
  if (!existing) {
    return getMemberById(id)
  }

  const merged = mergeMemberPatch(existing, patch)
  useMembersStore.getState().upsertMember(merged)
  return merged
}

async function updateMemberProfile(
  id: string,
  patch: MemberProfileUpdateInput
) {
  return updateMember(id, patch)
}

async function updateMemberAdmin(
  id: string,
  patch: Partial<MemberAdminUpdateInput>
) {
  return updateMember(id, patch)
}

async function createMemberFromOnboarding(input: MemberOnboardingInput) {
  const workspaceId = normalizeString(input.workspaceId) ?? resolveWorkspaceId()
  if (!workspaceId) {
    throw new Error("Workspace context is required to create members.")
  }

  const firstName = input.firstName.trim()
  if (!firstName) {
    throw new Error("First name is required.")
  }
  const lastName = (input.lastName ?? "").trim()
  const branchId = input.branchId.trim()
  if (!branchId) {
    throw new Error("Branch is required to create a member.")
  }

  const payload: {
    workspaceId: string
    branchId: string
    firstName: string
    middleName?: string | null
    lastName?: string | null
    gender: MemberOnboardingInput["gender"]
    status: MemberStatus
    email?: string | null
    phone?: string | null
    occupation?: string | null
    dateOfBirth?: string | null
    placeOfBirthCountry?: string | null
    placeOfBirthCity?: string | null
    placeOfBirth?: string | null
    maritalStatus?: Member["maritalStatus"] | null
    marriageType?: Member["marriageType"] | null
    baptized?: Member["baptized"] | null
    baptizedAt?: string | null
    baptismPlace?: string | null
    originChurchName?: string | null
    fatherFirstName?: string | null
    fatherMiddleName?: string | null
    fatherLastName?: string | null
    motherFirstName?: string | null
    motherMiddleName?: string | null
    motherLastName?: string | null
    spouseFirstName?: string | null
    spouseMiddleName?: string | null
    spouseLastName?: string | null
    guardianFirstName?: string | null
    guardianMiddleName?: string | null
    guardianLastName?: string | null
    address?: string | null
    notes?: string | null
    avatarUrl?: string | null
    groupIds: string[]
    ministryIds: string[]
    sourceFormSubmissionId?: string
  } = {
    workspaceId: workspaceId,
    branchId: branchId,
    firstName: firstName,
    lastName: lastName || null,
    gender: input.gender,
    status: input.status,
    groupIds: uniqueStrings((input.groupIds ?? []).map((value) => normalizeString(value))),
    ministryIds: uniqueStrings((input.ministryIds ?? []).map((value) => normalizeString(value))),
  }

  const email = normalizeString(input.email)
  if (email) payload.email = email
  if (input.email !== undefined && !email) payload.email = null

  const phone = normalizeString(input.phone)
  if (phone) payload.phone = phone
  if (input.phone !== undefined && !phone) payload.phone = null
  if (input.occupation !== undefined) payload.occupation = normalizePatchString(input.occupation)

  if (input.middleName !== undefined) payload.middleName = normalizePatchString(input.middleName)
  if (input.dateOfBirth !== undefined) payload.dateOfBirth = normalizePatchString(input.dateOfBirth)
  if (input.placeOfBirthCountry !== undefined) payload.placeOfBirthCountry = normalizePatchString(input.placeOfBirthCountry)
  if (input.placeOfBirthCity !== undefined) payload.placeOfBirthCity = normalizePatchString(input.placeOfBirthCity)

  const derivedPlaceOfBirth = [input.placeOfBirthCountry, input.placeOfBirthCity]
    .map((value) => normalizeString(value))
    .filter(Boolean)
    .join(", ")
  if (input.placeOfBirth !== undefined || derivedPlaceOfBirth) {
    payload.placeOfBirth = normalizePatchString(input.placeOfBirth ?? derivedPlaceOfBirth)
  }

  if (input.maritalStatus !== undefined) payload.maritalStatus = input.maritalStatus ?? null
  if (input.marriageType !== undefined) payload.marriageType = input.marriageType ?? null
  if (input.baptized !== undefined) payload.baptized = input.baptized ?? null
  if (input.baptizedAt !== undefined) payload.baptizedAt = normalizePatchString(input.baptizedAt)
  if (input.baptismPlace !== undefined) payload.baptismPlace = normalizePatchString(input.baptismPlace)
  if (input.originChurchName !== undefined) payload.originChurchName = normalizePatchString(input.originChurchName)
  if (input.fatherFirstName !== undefined) payload.fatherFirstName = normalizePatchString(input.fatherFirstName)
  if (input.fatherMiddleName !== undefined) payload.fatherMiddleName = normalizePatchString(input.fatherMiddleName)
  if (input.fatherLastName !== undefined) payload.fatherLastName = normalizePatchString(input.fatherLastName)
  if (input.motherFirstName !== undefined) payload.motherFirstName = normalizePatchString(input.motherFirstName)
  if (input.motherMiddleName !== undefined) payload.motherMiddleName = normalizePatchString(input.motherMiddleName)
  if (input.motherLastName !== undefined) payload.motherLastName = normalizePatchString(input.motherLastName)
  if (input.spouseFirstName !== undefined) payload.spouseFirstName = normalizePatchString(input.spouseFirstName)
  if (input.spouseMiddleName !== undefined) payload.spouseMiddleName = normalizePatchString(input.spouseMiddleName)
  if (input.spouseLastName !== undefined) payload.spouseLastName = normalizePatchString(input.spouseLastName)
  if (input.guardianFirstName !== undefined) payload.guardianFirstName = normalizePatchString(input.guardianFirstName)
  if (input.guardianMiddleName !== undefined) payload.guardianMiddleName = normalizePatchString(input.guardianMiddleName)
  if (input.guardianLastName !== undefined) payload.guardianLastName = normalizePatchString(input.guardianLastName)
  if (input.address !== undefined) payload.address = normalizePatchString(input.address)
  if (input.notes !== undefined) payload.notes = normalizePatchString(input.notes)
  if (input.avatarUrl !== undefined) payload.avatarUrl = normalizePatchString(input.avatarUrl)

  // Links the new member to the form submission it was created from (F-06). The backend's
  // createMemberSchema accepts this optional field and persists Member.sourceFormSubmissionId.
  const sourceFormSubmissionId = normalizeString(input.sourceFormSubmissionId)
  if (sourceFormSubmissionId) payload.sourceFormSubmissionId = sourceFormSubmissionId

  const response = await http.post<unknown>("/members", payload)
  const member = normalizeMember(extractMemberRecord(response.data), 0)
  useMembersStore.getState().upsertMember(member)

  await activityApi.recordActivityEvent({
    type: ActivityEventType.Member,
    action: "member.onboarded",
    title: "Member created",
    summary: `${member.name} member profile created`,
    actor: activityApi.resolveActorFromEmail(
      getAuthContextState().authMe?.auth.email
    ),
    target: {
      entityType: ActivityTargetEntityType.Member,
      entityId: member.id,
      label: member.name,
    },
    severity: ActivitySeverity.Info,
    occurredAt: new Date().toISOString(),
    metadata: {
      branchId: member.branchId ?? null,
      status: member.status,
    },
  })

  return member
}

async function updateMembers(ids: string[], patch: Partial<Member>) {
  const uniqueIds = Array.from(new Set(ids.map((id) => normalizeString(id)).filter((id): id is string => Boolean(id))))
  if (!uniqueIds.length) return []

  const updates = uniqueIds.map((id) => updateMember(id, patch))
  return Promise.all(updates)
}

async function deleteMember(id: string) {
  await http.delete(`/members/${id}`)
  useMembersStore.getState().deleteMember(id)
}

async function createInvite(memberId: string, email: string, actor: string) {
  const invite = await createInviteRecord(memberId, email, actor)
  const member = useMembersStore.getState().getMemberById(memberId)
  await activityApi.recordActivityEvent({
    type: ActivityEventType.Member,
    action: "member.invite.sent",
    title: "Invite sent",
    summary: `${member?.name ?? memberId} invite simulated`,
    actor: activityApi.resolveActorFromEmail(actor),
    target: {
      entityType: ActivityTargetEntityType.Member,
      entityId: memberId,
      label: member?.name ?? memberId,
    },
    severity: ActivitySeverity.Info,
    occurredAt: invite.sentAt,
    metadata: {
      inviteId: invite.id,
      expiresAt: invite.expiresAt,
      email: invite.email,
    },
  })
  return { ...invite }
}

async function listInvites() {
  return useMembersStore.getState().listInvites().map((invite) => ({ ...invite }))
}

async function getMemberDocuments(memberId: string) {
  void memberId
  return []
}

async function downloadMembersImportTemplate(
  workspaceId: string,
  format: "csv" | "xlsx" = "xlsx",
): Promise<{ blob: Blob; fileName: string }> {
  const response = await http.get<Blob>("/members/import/template", {
    params: { workspaceId, format },
    responseType: "blob",
  })
  const contentDisposition = response.headers?.["content-disposition"]
  const headerName = extractFilename(typeof contentDisposition === "string" ? contentDisposition : undefined)
  const fileName = headerName || (format === "xlsx" ? "members-import-template.xlsx" : "members-import-template.csv")
  return { blob: response.data, fileName }
}

/** Accepted values for the import UI: enum options (localized) + the existing groups/ministries to match. */
async function getMembersImportReference(workspaceId: string): Promise<MembersImportReference> {
  const response = await http.get<MembersImportReference>("/members/import/reference", {
    params: { workspaceId },
  })
  return response.data
}

/**
 * Download the flat, re-importable members CSV (id + member columns). This is the same shape the import
 * accepts, so the round-trip is: export this file, edit it, re-upload to update existing + add new members.
 */
async function downloadMembersDataExport(
  workspaceId: string,
  locale?: string,
  format: "csv" | "xlsx" = "csv",
): Promise<{ blob: Blob; fileName: string }> {
  const response = await http.get<Blob>("/members/export/data", {
    params: { workspaceId, format, ...(locale ? { locale } : {}) },
    responseType: "blob",
  })
  const contentDisposition = response.headers?.["content-disposition"]
  const headerName = extractFilename(typeof contentDisposition === "string" ? contentDisposition : undefined)
  const fileName = headerName || (format === "xlsx" ? "members.xlsx" : "members.csv")
  return { blob: response.data, fileName }
}

async function analyzeMembersImport(input: { workspaceId: string; file: File }): Promise<MembersImportAnalysis> {
  const formData = new FormData()
  formData.append("workspaceId", input.workspaceId)
  formData.append("file", input.file)
  const response = await http.post<MembersImportAnalysis>("/members/import/analyze", formData)
  return response.data
}

async function previewMembersImport(input: {
  workspaceId: string
  file: File
  mapping?: Record<string, string>
}): Promise<MembersImportAnalysis> {
  const formData = new FormData()
  formData.append("workspaceId", input.workspaceId)
  formData.append("file", input.file)
  if (input.mapping && Object.keys(input.mapping).length > 0) {
    formData.append("mapping", JSON.stringify(input.mapping))
  }
  const response = await http.post<MembersImportAnalysis>("/members/import/preview", formData)
  return response.data
}

async function confirmMembersImport(input: {
  workspaceId: string
  file: File
  mapping?: Record<string, string>
  importValidRowsOnly?: boolean
  groupResolutions?: MembersImportResolution[]
}): Promise<MembersImportConfirmResult> {
  const formData = new FormData()
  formData.append("workspaceId", input.workspaceId)
  formData.append("file", input.file)
  if (input.mapping && Object.keys(input.mapping).length > 0) {
    formData.append("mapping", JSON.stringify(input.mapping))
  }
  if (typeof input.importValidRowsOnly === "boolean") {
    formData.append("importValidRowsOnly", String(input.importValidRowsOnly))
  }
  if (input.groupResolutions && input.groupResolutions.length > 0) {
    formData.append("groupResolutions", JSON.stringify(input.groupResolutions))
  }
  const response = await http.post<MembersImportConfirmResult>("/members/import/confirm", formData)
  return response.data
}

export const membersApi = {
  listMembers,
  getMembersOverview,
  getMemberById,
  getMemberRelationships,
  decideRelationship,
  updateMember,
  updateMemberProfile,
  updateMemberAdmin,
  createMemberFromOnboarding,
  updateMembers,
  deleteMember,
  createInvite,
  listInvites,
  getMemberDocuments,
  downloadMembersImportTemplate,
  getMembersImportReference,
  downloadMembersDataExport,
  analyzeMembersImport,
  previewMembersImport,
  confirmMembersImport,
}
